import { offsetMilliseconds, zonedParts } from './time-zone.ts';

/** A calendar date (YYYY-MM-DD) in the business time zone. No time-of-day, no zone attached. */
export type BusinessDate = string & { readonly __brand: 'BusinessDate' };

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function components(date: BusinessDate): [number, number, number] {
  const match = DATE_PATTERN.exec(date);
  if (!match) throw new RangeError(`Not a business date: ${date}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function format(year: number, month: number, day: number): BusinessDate {
  const pad = (n: number, width: number) => String(n).padStart(width, '0');
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}` as BusinessDate;
}

/** Validates a `YYYY-MM-DD` string, rejecting impossible dates such as 2026-02-30. */
export function parseBusinessDate(value: string): BusinessDate {
  const match = DATE_PATTERN.exec(value);
  if (!match) throw new RangeError(`Expected YYYY-MM-DD, got "${value}"`);
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(year, month - 1, day));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    throw new RangeError(`Invalid calendar date "${value}"`);
  }
  return value as BusinessDate;
}

/** The business date on which an instant falls in the given time zone. */
export function toBusinessDate(instant: Date, timeZone: string): BusinessDate {
  const p = zonedParts(instant, timeZone);
  return format(p.year, p.month, p.day);
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  const [year, month, day] = components(date);
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * DAY_MS);
  return format(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** Whole calendar days from `from` to `to` (negative if `to` is earlier). */
export function daysBetween(from: BusinessDate, to: BusinessDate): number {
  const [y1, m1, d1] = components(from);
  const [y2, m2, d2] = components(to);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY_MS);
}

/**
 * The first instant (UTC) of a business date in the given time zone.
 * Handles zones whose midnight is skipped by a DST transition by returning the first existing instant.
 */
export function startOfBusinessDate(date: BusinessDate, timeZone: string): Date {
  const [year, month, day] = components(date);
  const midnightAsUtc = Date.UTC(year, month - 1, day);

  // Offsets around the target day; at most two distinct values exist near a transition.
  const offsets = new Set([
    offsetMilliseconds(new Date(midnightAsUtc - DAY_MS), timeZone),
    offsetMilliseconds(new Date(midnightAsUtc), timeZone),
    offsetMilliseconds(new Date(midnightAsUtc + DAY_MS), timeZone),
  ]);

  const candidates = [...offsets]
    .map((offset) => midnightAsUtc - offset)
    .filter((t) => toBusinessDate(new Date(t), timeZone) === date)
    .sort((a, b) => a - b);

  const exactMidnight = candidates.find((t) => {
    const p = zonedParts(new Date(t), timeZone);
    return p.hour === 0 && p.minute === 0 && p.second === 0;
  });
  if (exactMidnight !== undefined) return new Date(exactMidnight);

  // Midnight does not exist (spring-forward at 00:00): the day starts at the transition instant.
  const earliest = candidates[0];
  if (earliest === undefined) {
    throw new Error(`Cannot determine start of ${date} in ${timeZone}`);
  }
  let low = earliest - DAY_MS / 24;
  let high = earliest;
  while (high - low > 1000) {
    const mid = Math.floor((low + high) / 2);
    if (toBusinessDate(new Date(mid), timeZone) === date) high = mid;
    else low = mid;
  }
  return new Date(high);
}
